// step 4 of the sponsor pledge flow (review & submit)

import React from 'react'
import { Trans } from 'react-i18next'
import Icon from '../../../../components/icon'
import { formatAmount } from '../../../../lib/numbers'

export default function Step4ReviewSubmit({
  styles,
  t,
  eventTitle,
  formattedLocation,
  volunteerCount,
  estimatedVolunteerHours,
  applicationDateStr,
  commitmentNum,
  vpfAmount,
  mpfAmount,
  opsNum,
  reviewSubtotal,
  paymentDeadlineStr,
  bizName,
  contactName,
  bizWeb,
  orgMessage,
  adminAmt,
  serviceAmt,
  achFee,
  cardFee,
  totalAch,
  tosAccepted,
  setTosAccepted,
  openStripeModal,
  setStep,
}) {
  return (
    <>
      <h1 className={styles.stepHeading}>
        {t('sponsorPledgeFlow.step5.heading')}
      </h1>
      <p className={styles.stepSubheading}>
        {t('sponsorPledgeFlow.step5.subheading')}
      </p>

      <div className={styles.sectionLabel}>
        {t('sponsorPledgeFlow.step5.summarySection')}
      </div>
      <div className={styles.reviewCard}>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.rowSponsorship')}
          </span>
          <span className={styles.reviewVal}>
            {t('sponsorPledgeFlow.tier.name')}
          </span>
        </div>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.rowEvent')}
          </span>
          <span className={styles.reviewVal}>
            {eventTitle}
            {formattedLocation ? ` · ${formattedLocation}` : ''}
          </span>
        </div>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.rowVolunteers')}
          </span>
          <span className={styles.reviewVal}>
            {t('sponsorPledgeFlow.header.volunteerStats', {
              count: volunteerCount,
              hours: estimatedVolunteerHours ?? '—',
            })}
          </span>
        </div>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.rowAppDate')}
          </span>
          <span className={styles.reviewVal}>{applicationDateStr}</span>
        </div>
      </div>

      <div className={styles.sectionLabel}>
        {t('sponsorPledgeFlow.step5.detailsSection')}
      </div>
      <div className={styles.reviewCard}>
        <div className={styles.reviewPfCols}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {t('sponsorPledgeFlow.step5.commitment')}
            </div>
            <div style={{ fontWeight: 700 }}>
              {formatAmount(commitmentNum, true)}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {t('sponsorPledgeFlow.step5.payForward')}
            </div>
            <div>
              {t('sponsorPledgeFlow.step5.vpfLine')}{' '}
              <strong>{formatAmount(vpfAmount, true)}</strong>
            </div>
            <div>
              {t('sponsorPledgeFlow.step5.mpfLine')}{' '}
              <strong>{formatAmount(mpfAmount, true)}</strong>
            </div>
          </div>
        </div>
        {opsNum > 0 ? (
          <div className={styles.reviewRow}>
            <span className={styles.reviewLbl}>
              {t('sponsorPledgeFlow.step5.opsRow')}
            </span>
            <span className={styles.reviewVal}>
              {formatAmount(opsNum, true)}
            </span>
          </div>
        ) : null}
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.subtotal')}
          </span>
          <span className={styles.reviewVal}>
            {formatAmount(reviewSubtotal, true)}
          </span>
        </div>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.deadline')}
          </span>
          <span className={styles.reviewVal}>{paymentDeadlineStr}</span>
        </div>
      </div>

      <div className={styles.sectionLabel}>
        {t('sponsorPledgeFlow.step5.brandSection')}
      </div>
      <div className={styles.reviewCard}>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.biz')}
          </span>
          <span className={styles.reviewVal}>{bizName || '—'}</span>
        </div>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.contact')}
          </span>
          <span className={styles.reviewVal}>{contactName || '—'}</span>
        </div>
        <div className={styles.reviewRow}>
          <span className={styles.reviewLbl}>
            {t('sponsorPledgeFlow.step5.website')}
          </span>
          <span className={styles.reviewVal}>{bizWeb.trim() || '—'}</span>
        </div>
        {orgMessage?.trim() ? (
          <div
            className={styles.reviewRow}
            style={{ alignItems: 'flex-start' }}
          >
            <span className={styles.reviewLbl}>
              {t('sponsorPledgeFlow.step3.organizerMessage')}
            </span>
            <span
              className={styles.reviewVal}
              style={{ whiteSpace: 'pre-wrap', textAlign: 'right' }}
            >
              {orgMessage.trim()}
            </span>
          </div>
        ) : null}
      </div>

      <div className={styles.sectionLabel}>
        {t('sponsorPledgeFlow.step5.costSection')}
      </div>
      <div className={styles.reviewCard}>
        <div className={styles.costRow}>
          <span>{t('sponsorPledgeFlow.step5.subtotal')}</span>
          <strong>{formatAmount(reviewSubtotal, true)}</strong>
        </div>
        {adminAmt > 0 ? (
          <div className={styles.costRow}>
            <span>{t('sponsorPledgeFlow.step5.adminFee')}</span>
            <strong>{formatAmount(adminAmt, true)}</strong>
          </div>
        ) : null}
        {serviceAmt > 0 ? (
          <div className={styles.costRow}>
            <span>{t('sponsorPledgeFlow.step5.serviceFee')}</span>
            <strong>{formatAmount(serviceAmt, true)}</strong>
          </div>
        ) : null}
        <div className={styles.costRow}>
          <span>{t('sponsorPledgeFlow.step5.stripeFees')}</span>
          <span style={{ textAlign: 'right' }}>
            <strong>ACH</strong> {formatAmount(achFee, true)}
            <br />
            <span style={{ fontSize: 12, color: 'var(--light)' }}>
              {t('sponsorPledgeFlow.step5.orCard')}{' '}
              {formatAmount(cardFee, true)}
            </span>
          </span>
        </div>
        <div className={styles.costTotalRow}>
          <span>{t('sponsorPledgeFlow.step5.totalDueAch')}</span>
          <strong>{formatAmount(totalAch, true)}</strong>
        </div>
      </div>

      <div
        className={styles.tosRow}
        onClick={() => setTosAccepted(v => !v)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setTosAccepted(v => !v)
          }
        }}
        role="checkbox"
        aria-checked={tosAccepted}
        tabIndex={0}
      >
        <div
          className={[styles.tosBox, tosAccepted ? styles.tosBoxChecked : '']
            .filter(Boolean)
            .join(' ')}
        >
          {tosAccepted ? <Icon name="check" style={{ fontSize: 10 }} /> : null}
        </div>
        <span>
          <Trans
            i18nKey="sponsorPledgeFlow.step5.tos"
            components={{
              1: (
                <a
                  href="https://civicrush.com/terms-of-service/"
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                />
              ),
            }}
          />
        </span>
      </div>

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => setStep(3)}
        >
          ‹ {t('buttons.back')}
        </button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 16,
            flex: 1,
          }}
        >
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={!tosAccepted}
            onClick={() => openStripeModal()}
          >
            {t('sponsorPledgeFlow.step5.submitPledge')}
          </button>
        </div>
      </div>
    </>
  )
}
